// components/auth/OAuthSuccessPage.js
import React, { useEffect, useState, useRef } from 'react';
import { MessageCircle, CheckCircle, XCircle } from 'lucide-react';
import { trackSignupConversion } from '../../utils/analytics';

const OAuthSuccessPage = ({ onLoginSuccess, api }) => {
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your login...');
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent double-processing in Strict Mode
    if (hasProcessedRef.current) return;
    hasProcessedRef.current = true;

    const handleOAuthCallback = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        const userStr = urlParams.get('user');
        const workspaceStr = urlParams.get('workspace');
        const subscriptionStr = urlParams.get('subscription');
        const error = urlParams.get('error');
        const errorMessage = urlParams.get('message');

        console.log('OAuth callback - Checking URL params:', {
          hasToken: !!token,
          hasUser: !!userStr,
          hasError: !!error,
          fullURL: window.location.href
        });

        // Check for errors first
        if (error) {
          setStatus('error');
          setMessage(errorMessage || getErrorMessage(error));
          return;
        }

        // Check for required data
        if (!token || !userStr) {
          console.error('Missing OAuth data - token:', !!token, 'user:', !!userStr);
          setStatus('error');
          setMessage('Missing authentication data. Please try logging in again.');
          
          // Auto-redirect to login after 3 seconds
          setTimeout(() => {
            window.location.href = '/login';
          }, 3000);
          return;
        }

        // Parse user data
        const user = JSON.parse(decodeURIComponent(userStr));
        const workspace = workspaceStr ? JSON.parse(decodeURIComponent(workspaceStr)) : null;
        const subscription = subscriptionStr ? JSON.parse(decodeURIComponent(subscriptionStr)) : null;

        console.log('OAuth success - parsed data:', {
          userId: user?.id,
          userEmail: user?.email,
          workspaceId: workspace?.id,
          hasSubscription: !!subscription
        });

        // Set token in API service
        api.setToken(token);

        // Set workspace and subscription if available
        if (workspace) {
          api.setCurrentWorkspace(workspace);
        }
        if (subscription) {
          api.setUserSubscription(subscription);
        }

        setStatus('success');
        // Different messaging for existing vs new users
        const welcomeMessage = user.isExistingUser
          ? 'Welcome back! Login successful.'
          : 'Account created successfully! Welcome to TicketDesk.';
        setMessage(welcomeMessage);

        // Track Google Analytics conversion for new signups only
        if (!user.isExistingUser) {
          trackSignupConversion({
            method: 'google',
            plan: subscription?.plan || 'TRIAL',
            userId: user.id,
            isNewUser: true
          });
        }

        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);

        // Call success callback after short delay
        setTimeout(() => {
          onLoginSuccess(user);
        }, 1000);

      } catch (error) {
        console.error('OAuth callback processing error:', error);
        setStatus('error');
        setMessage('Failed to process login. Please try again.');
        
        // Auto-redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [onLoginSuccess, api]);

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'oauth_failed':
        return 'Google authentication failed. Please try again.';
      case 'no_user':
        return 'Unable to retrieve user information from Google.';
      case 'callback_failed':
        return 'Authentication callback failed. Please try again.';
      default:
        return 'An authentication error occurred. Please try again.';
    }
  };

  const retry = () => {
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl mb-4 shadow-lg">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">TicketDesk</h1>
        </div>

        {/* Status Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === 'processing' && (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Processing Login
              </h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {message.includes('created') ? 'Account Created!' : 'Login Successful!'}
              </h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Authentication Failed
              </h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <button
                onClick={retry}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default OAuthSuccessPage;